import {
  TestType,
  PercentagePoint,
  Position,
  StimulusResponseState,
  StimulusResponse,
  Stimulus,
  StimulusType,
  PercentageWithTimeStamp,
  DistanceData,
  CPTData,
  VFABehaviour,
  GazeWithDelta,
} from "types/testTypes";
import {
  isBinocularVFATestType,
  isStandardVFATestType,
} from "@constants/testConstants/testSpecificConstants";

export function getFDTDegreeValue(testType: TestType): string | undefined {
  if (!testType) return undefined;
  const degree = testType.split("-")[1]?.replace(/-[15]/, "");
  if (degree && degree !== "30" && degree !== "20") {
    throw new Error(`Invalid degree value: ${degree} for test type: ${testType}`);
  }
  return degree;
}

export function isPercentagePoint(
  point: Position | PercentagePoint | undefined,
): point is PercentagePoint {
  if (!point) return false;
  return "xPercentage" in point && "yPercentage" in point;
}

/**
 * Normalizes gaze data to ensure consistent format across the application
 * Converts array format [x, y] to object format {xPercentage, yPercentage}
 * This function is created specifically for mapping the results we receive from DY test
 */
export function normalizeGazeData(data: any): PercentagePoint {
  console.log("normalizeGazeData", data);
  if (isPercentagePoint(data)) {
    return data;
  } else if (Array.isArray(data)) {
    return {
      xPercentage: data[0],
      yPercentage: data[1],
    };
  }
}

/**
 * Normalizes a GazeWithDelta item
 */
export function normalizeGazeWithDelta(item: any): GazeWithDelta {
  return {
    gazePercentageData: normalizeGazeData(item.gazePercentageData),
    delta: item.gazeDelta || item.delta, // Handle both field names
    gazeDirectionVector: item.gazeDirectionVector,
    leftGazeDirectionVector: item.leftGazeDirectionVector,
    rightGazeDirectionVector: item.rightGazeDirectionVector,
    gazeOrigin: item.gazeOrigin,
    headPosition: item.headPosition,
    headAngularSpeed: item.headAngularSpeed,
    leftEyeOpenness: item.leftEyeOpenness,
    rightEyeOpenness: item.rightEyeOpenness,
    leftPupilMm: item.leftPupilMm,
    rightPupilMm: item.rightPupilMm,
  };
}

/**
 * Transforms the test result data from API format to frontend format
 */
export function transformDyslexiaTestResult(result: any) {
  if (!result?.results?.gazeDataWithDeltaList) return result;

  return {
    ...result,
    results: {
      ...result.results,
      gazeDataWithDeltaList: result.results.gazeDataWithDeltaList.map(normalizeGazeWithDelta),
    },
  };
}

export function transformCPTResult(result: any) {
  if (!result?.results) return result;

  const cptData = result.results as CPTData;

  if (cptData.eyeDisplacements) {
    return result;
  }

  let eyeDisplacements: DistanceData[] = [];

  if (cptData.gazePercentageData && cptData.gazePercentageData.length > 0) {
    eyeDisplacements = cptData.gazePercentageData.map((point: PercentagePoint, index: number) => ({
      distance: calculateDisplacementFromOrigin(point),
      // If no timestamp is available, use the index as a fallback -> WOW nice thinking ai...
      timeStamp: (point as PercentageWithTimeStamp).timeStamp || index,
    }));
  }

  return {
    ...result,
    results: {
      ...cptData,
      eyeDisplacements,
    },
  };
}

export function transformFDTResult(result: any) {
  if (!result?.fovea) return result;

  const { contrast, ...restFovea } = result.fovea;
  return {
    ...result,
    fovea: {
      ...restFovea,
      value: contrast,
    },
  };
}

/**
 * Formats the received test entry for Continuous Performance Test (CPT)
 */
export function groupStimulusResponses(
  responses?: StimulusResponse[] | null,
): Record<string, { time: number; delta: number }[]> {
  const responseGroups = {
    correctResponse: StimulusResponseState.Correct,
    correctMultiPressResponse: StimulusResponseState.CorrectMultiPress,
    wrongResponse: StimulusResponseState.Incorrect,
    wrongMultiPressResponse: StimulusResponseState.IncorrectMultiPress,
    missResponse: StimulusResponseState.Miss,
    falsePositiveEmptyResponse: StimulusResponseState.FalsePositiveEmpty,
    falsePositiveAudioResponse: StimulusResponseState.FalsePositiveAudio,
    falsePositiveVisualResponse: StimulusResponseState.FalsePositiveVisual,
    falsePositiveAudioVisualResponse: StimulusResponseState.FalsePositiveAudioVisual,
  };

  const groupedResponses: Record<string, { time: number; delta: number }[]> = {};
  Object.keys(responseGroups).forEach((key) => {
    groupedResponses[key] =
      responses
        ?.filter(
          (response) => response.state === responseGroups[key as keyof typeof responseGroups],
        )
        .map((response) => ({
          time: response.timeStamp,
          delta: response.responseDelta,
        })) || [];
  });

  return groupedResponses;
}

/**
 * Groups stimuli by their type
 * @param stimuli - Array of Stimulus objects
 * @return An object with keys as StimulusType and values as arrays of Stimulus
 * @description This function categorizes stimuli into Target, NonTarget, VisualDistractor, and AudioDistractor.
 */
export function groupStimuli(stimuli?: Stimulus[] | null): Record<StimulusType, Stimulus[]> {
  const groupedStimuli: Record<StimulusType, Stimulus[]> = {
    [StimulusType.Target]: [],
    [StimulusType.NonTarget]: [],
    [StimulusType.VisualDistractor]: [],
    [StimulusType.AudioDistractor]: [],
    [StimulusType.AudioVisualDistractor]: [],
  };

  if (stimuli) {
    stimuli.forEach((stimulus) => {
      if (stimulus.type && groupedStimuli[stimulus.type as StimulusType]) {
        groupedStimuli[stimulus.type as StimulusType].push(stimulus);
      }
    });
  }

  return groupedStimuli;
}

export function calculateDisplacementFromOrigin(point1: PercentagePoint): number {
  const dx = point1.xPercentage - 50;
  const dy = point1.yPercentage - 50;
  return Math.round(Math.sqrt(dx * dx + dy * dy) * 100) / 100;
}

export function isBinocularTest(testType: TestType): boolean {
  return isBinocularVFATestType(testType);
}
export function isFixationChanging(testType: TestType): boolean {
  return (
    testType === TestType.PER_FF_120 ||
    testType === TestType.PER_SUPERIOR36 ||
    testType === TestType.PER_SUPERIOR64 ||
    isBinocularTest(testType)
  );
}

export function isSimpleTest(testType: TestType, testBehaviour: VFABehaviour) {
  const isNormalTest = isStandardVFATestType(testType);
  return (
    testType === TestType.PER_FF_120 ||
    testType === TestType.PER_C_40 ||
    testType === TestType.PER_C_76 ||
    testType === TestType.PER_SUPERIOR36 ||
    testType === TestType.PER_SUPERIOR64 ||
    (testBehaviour == VFABehaviour.SIMPLE_TEST && !isNormalTest)
  );
}

export function isThreeZoneBehaviour(testBehaviour: VFABehaviour | string): boolean {
  return testBehaviour?.toString() === VFABehaviour.THREE_ZONE.toString();
}

export function isZoneTestBehaviour(testBehaviour: VFABehaviour | string): boolean {
  const value = testBehaviour?.toString();
  return value === VFABehaviour.TWO_ZONE.toString() || value === VFABehaviour.THREE_ZONE.toString();
}

export function isSuprathresholdBehaviour(
  testBehaviour: VFABehaviour | string | number | null | undefined,
): boolean {
  return (
    testBehaviour?.toString() === VFABehaviour.SIMPLE_TEST.toString() ||
    isZoneTestBehaviour(testBehaviour)
  );
}

// Eye tracking cannot follow these markers, so the headset counts losses with blind spot catch trials.
export function countsFixationLossesOnMovingFixation(testType: TestType): boolean {
  return testType === TestType.PER_FF_120 || isBinocularTest(testType);
}

export function hasBlindSpotCatchTrials(entry: { blindSpotTotal?: unknown }): boolean {
  return Number(entry.blindSpotTotal) > 0;
}

// The curve ranks dB thresholds against a normative profile, so it only reads
// correctly for the strategies that measure a real threshold at every location.
const BEBIE_CURVE_VFA_BEHAVIOURS = [
  VFABehaviour.FAST_4_2,
  VFABehaviour.FAST_4,
  VFABehaviour.FULL_THRESH_4,
  VFABehaviour.SITA_LIKE,
];

export function supportsBebieCurve(
  testBehaviour: VFABehaviour | string | null | undefined,
): boolean {
  if (testBehaviour === null || testBehaviour === undefined) {
    return false;
  }
  const value = testBehaviour.toString();
  return BEBIE_CURVE_VFA_BEHAVIOURS.some((behaviour) => behaviour.toString() === value);
}

export type ZoneSymbolKind = "normal" | "relative" | "absolute" | null;

export function getZoneSymbolKind(
  value: unknown,
  testBehaviour: VFABehaviour | string,
): ZoneSymbolKind {
  if (value === null || value === undefined || value === -1) {
    return null;
  }
  const numericValue = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(numericValue)) {
    return null;
  }
  if (isThreeZoneBehaviour(testBehaviour)) {
    if (numericValue === 2) return "normal";
    if (numericValue === 1) return "relative";
    return "absolute";
  }
  return numericValue === 1 ? "normal" : "absolute";
}

export interface ZoneStats {
  pointsSeen: number;
  pointsMissed: number;
  efficiencyScore: number;
}

export function computeZoneStats(
  kinds: (ZoneSymbolKind | "none")[],
  excludedIndexes: number[] = [],
): ZoneStats {
  const excluded = new Set(excludedIndexes);
  const countedKinds = kinds.filter((_kind, index) => !excluded.has(index));
  const pointsSeen = countedKinds.filter((k) => k === "normal").length;
  const pointsMissed = countedKinds.filter((k) => k === "relative" || k === "absolute").length;
  const total = pointsSeen + pointsMissed;
  const efficiencyScore = total > 0 ? Number(((100 * pointsSeen) / total).toPrecision(2)) : 0;
  return { pointsSeen, pointsMissed, efficiencyScore };
}
